/**
 * Small formatters for the garage's numbers, and the fuel gauge's arithmetic.
 *
 * This was `fuel-display.ts`. Fuel was deleted and the file went with it, which
 * was too eager — `formatCountdown`, `boostRemaining` and `formatPrice` were
 * never about fuel, so they were rescued here under an honest name. Fuel is back
 * and the arithmetic comes back **here** rather than to the old filename, for
 * the reason the rename was right in the first place: `formatCountdown` now has
 * two callers, the gauge below and the *research* timer, and a second copy of it
 * in a fuel-shaped file is a formatter that gets fixed in one place and not the
 * other.
 *
 * ── The rule both countdowns obey ────────────────────────────────────────────
 * **Count down locally, never decide.** A remainder from the server — fuel's
 * `secondsToNextFuel`, a research's `secondsRemaining` — is true at the instant
 * of the response and starts ageing immediately. The client shows that ageing
 * honestly and, when it reaches zero, *asks* rather than concludes. Adding one
 * to the displayed fuel, or flipping a node to researched, would be the client
 * deciding what a player owns, which is the sort of small local truth that
 * drifts from the server's and then has to be reconciled by a page reload.
 *
 * Pure, so all of it is testable without a component or a clock.
 */

/** As much of a `Wallet` as the gauge needs. */
export interface FuelSnapshot {
  fuel: number;
  fuelCapacity: number;
  /** Seconds until the next unit at `takenAtMs`. Null when full. */
  secondsToNextFuel: number | null;
  fuelRegenSeconds: number;
}

export interface FuelCountdown {
  fuel: number;
  capacity: number;
  /** 0–1, for the gauge. */
  fill: number;
  /** Whole seconds left, clamped at zero. Null when the tank is full. */
  secondsRemaining: number | null;
  /** Formatted `m:ss`, or null when the tank is full. */
  label: string | null;
  /** True once the countdown has run out and the server should be re-read. */
  elapsed: boolean;
  full: boolean;
}

/**
 * Where the countdown stands, `sinceMs` milliseconds after the snapshot was
 * taken.
 */
export function fuelCountdown(snapshot: FuelSnapshot, sinceMs: number): FuelCountdown {
  const capacity = Math.max(1, snapshot.fuelCapacity);
  const fuel = Math.max(0, Math.min(capacity, snapshot.fuel));
  const full = fuel >= capacity || snapshot.secondsToNextFuel === null;

  if (full) {
    return {
      fuel,
      capacity,
      fill: fuel / capacity,
      secondsRemaining: null,
      label: null,
      elapsed: false,
      full: true,
    };
  }

  const elapsedSeconds = Math.max(0, Math.floor(sinceMs / 1000));
  const remaining = (snapshot.secondsToNextFuel ?? 0) - elapsedSeconds;

  return {
    fuel,
    capacity,
    fill: fuel / capacity,
    secondsRemaining: Math.max(0, remaining),
    label: formatCountdown(Math.max(0, remaining)),
    // The moment this is true the caller re-reads the wallet rather than
    // guessing at the new balance.
    elapsed: remaining <= 0,
    full: false,
  };
}

/** `m:ss`, or `h:mm:ss` past an hour. Never a bare seconds count past 60. */
export function formatCountdown(totalSeconds: number): string {
  const total = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * How much longer a boost runs, from an ISO instant.
 *
 * Returns null for an absent, unparseable or already-past expiry — an expired
 * boost and no boost are the same thing to a player, and the server has already
 * filtered the expired ones out.
 */
export function boostRemaining(expiresAt: string | null, nowMs: number): string | null {
  if (!expiresAt) return null;
  const end = Date.parse(expiresAt);
  if (!Number.isFinite(end)) return null;

  const seconds = Math.floor((end - nowMs) / 1000);
  if (seconds <= 0) return null;

  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3600);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${Math.floor((seconds % 3600) / 60)}m`;
  return formatCountdown(seconds);
}

/**
 * Minor units to a displayable price. Money is never a float in transit.
 *
 * **`Intl` rather than a symbol table, since the shop stopped being dollars.**
 * It used to hard-code `$` for USD and print the code for anything else, which
 * was right while every price in `premium.data.ts` was the only currency on
 * screen; with `PricingService` quoting a player's own currency, "EUR 17.23"
 * beside "$19.99" is two conventions in one card. `Intl` gives the narrow symbol
 * per currency and gets the grouping right for large figures — the custom-pack
 * slider reaches $1,600.
 *
 * The locale is deliberately fixed rather than the browser's: the UI is English
 * throughout, and a machine set to German would otherwise render `1.600,00 $`
 * next to English copy. What varies here is the *currency*, which is the player's
 * explicit choice, not the number formatting, which is the interface's.
 *
 * A currency `Intl` refuses degrades to the old `CODE 0.00` form rather than
 * throwing — a price is not the place to raise.
 */
export function formatPrice(cents: number, currency: string): string {
  const major = Math.max(0, Math.round(cents)) / 100;
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    }).format(major);
  } catch {
    return `${currency} ${major.toFixed(2)}`;
  }
}
